import React, { useEffect, useState } from 'react'
import { Grid, Text } from '@adsk/alloy-react-box'
import FormField from '@adsk/alloy-react-form-field'
import Tag from '@adsk/alloy-react-tag'
import Theme from '@adsk/alloy-react-theme'
import styled from 'styled-components'
import { INFO_MESSAGES, SELECTION_LIST_KEYS } from '../../constants'
import _ from 'lodash'
import TextInput from '@adsk/alloy-react-text-input'
import { track } from '@adsk/acs-web-analytics'
import ANALYTICS_TYPES from '../../types/AnalyticsTypes'
import { getPackageInfo } from '../../utils'
import { useTranslation } from '../../../i18n'
import {
	OnboardingUserAppContextType,
	useOnboardingUserContext,
} from '../../store'
import { setMarketingCompanyType, setUserInfo } from '../../store/actions'
import { GetSelectionListByNameResult } from '@adsk/napi-api-client'
import { SUPPORTED_VALIDATION_KEYS } from '../../utils/validation'
import ConditionalFlyoutComponent from '../ConditionalFlyoutComponent'
import { AlertWarningIcon } from '@adsk/alloy-react-icon'

const DisclaimerDiv = styled.div`
	font-size: 0.75rem;
	font-style: italic;
	color: ${Theme.colors.black};
`
const CompanyTypeContainer = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
`
const DataLoadErrorDiv = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	padding: 6px;
`

interface MarketingComponentProps {
	displayNameErrorComponent: (
		key: SUPPORTED_VALIDATION_KEYS,
		value?: string
	) => JSX.Element | null | false
	invalidFields: string[]
}

const MarketingComponent: React.FC<MarketingComponentProps> = ({
	displayNameErrorComponent,
	invalidFields,
}): JSX.Element => {
	const { name, version } = getPackageInfo()
	const { t } = useTranslation()
	const { typography, colors } = Theme
	const {
		dispatch,
		state: { marketingCompanyType, userInfo, flowType, selectionsList },
	} = useOnboardingUserContext() as OnboardingUserAppContextType

	const [selectionListItems] =
		useState<GetSelectionListByNameResult[]>(selectionsList)

	const [companyTypeList, setCompanyTypeList] = useState<string[] | undefined>(
		undefined
	)
	const prePopulatedCompanyName = _.get(
		userInfo,
		'companyName',
		''
	) as unknown as string

	useEffect(() => {
		selectionListItems?.forEach((constantList) => {
			if (constantList.name === SELECTION_LIST_KEYS.COMPANY_TYPES) {
				let list = _.map(constantList.optionsList, 'value')
				setCompanyTypeList(list)
				const transformedCompanyType = (userInfo?.companyType ?? []).reduce(
					(returnVal: any, key) => {
						returnVal[key] = true
						return returnVal
					},
					{}
				)
				const remainingCompanyTypes = _.difference(userInfo?.companyType, list)
				dispatch(
					setMarketingCompanyType({
						...transformedCompanyType,
						...(remainingCompanyTypes.length > 0 && {
							Other: true,
						}),
					})
				)
			}
		})
	}, [])

	const onSelectCompanyType = (ct: string) => {
		dispatch(
			setMarketingCompanyType({
				...marketingCompanyType,
				[ct]: !marketingCompanyType[ct],
			})
		)
	}

	const lockedFields = _.get(userInfo, 'lockedFields', []) as string[]
	return (
		<React.Fragment>
			<Grid
				gridGap={2}
				marginTop={'2%'}
				marginBottom={'5%'}
				data-testid={'marketingComponent'}
			>
				<FormField
					label={`${t('COMPANY_NAME')}`}
					labelVariant={FormField.LABEL_VARIANTS.TOP}
					style={typography.bodySmall}
					errorVariant={'caption'}
					required={true}
					error={displayNameErrorComponent(
						'companyName',
						prePopulatedCompanyName
					)}
				>
					<ConditionalFlyoutComponent
						message={t('TOOL_TIP_COMPANY_DISABLED_MESSAGE')}
						show={lockedFields.includes('companyName')}
					>
						<TextInput
							type={TextInput.TYPES.TEXT}
							value={prePopulatedCompanyName}
							error={
								!_.isEmpty(prePopulatedCompanyName) &&
								invalidFields.includes('companyName')
							}
							data-testid={'companyNameField'}
							placeholder={`${t('PLACEHOLDER_COMPANY')}`}
							disabled={lockedFields.includes('companyName')}
							onChange={(e) => {
								track(ANALYTICS_TYPES.NAPI_USERS_MODIFIED_ONBOARDED_INFO, {
									package: name,
									version,
									flowType,
									fieldName: 'companyName',
								})
								dispatch(
									setUserInfo({
										...userInfo,
										companyName: e.target.value,
									})
								)
							}}
						/>
					</ConditionalFlyoutComponent>
				</FormField>
				<FormField
					label={t('COMPANY_TYPE')}
					required={true}
					labelVariant={FormField.LABEL_VARIANTS.TOP}
					style={typography.bodySmall}
				>
					{selectionsList.length > 0 ? (
						<ConditionalFlyoutComponent
							message={t('TOOL_TIP_COMPANY_DISABLED_MESSAGE')}
							show={lockedFields.includes('companyType')}
						>
							<CompanyTypeContainer>
								{companyTypeList?.map((type, i) => (
									<Tag
										key={i}
										data-testid={type}
										disabled={lockedFields.includes('companyType')}
										style={{
											margin: 2,
										}}
										selected={marketingCompanyType[type]}
										onChange={() => {
											track(
												ANALYTICS_TYPES.NAPI_USERS_MODIFIED_MARKETING_INFO,
												{
													package: name,
													version,
													flowType,
													fieldName: 'companyType',
												}
											)
											onSelectCompanyType(type)
										}}
									>
										{type}
									</Tag>
								))}
							</CompanyTypeContainer>
						</ConditionalFlyoutComponent>
					) : (
						<DataLoadErrorDiv>
							<AlertWarningIcon size={14} />
							<Text
								style={{
									...typography.bodySmall,
									color: colors.charcoal700,
									paddingLeft: 10,
								}}
							>
								{t('VIEW_EDIT_MODAL_LOAD_ERROR')}
							</Text>
						</DataLoadErrorDiv>
					)}
				</FormField>
				{INFO_MESSAGES.marketing.DISCLAIMER && (
					<DisclaimerDiv>
						<span>{INFO_MESSAGES.marketing.DISCLAIMER}</span>
					</DisclaimerDiv>
				)}
			</Grid>
		</React.Fragment>
	)
}

export default MarketingComponent
